import { Link } from "react-router-dom";
import { ChevronLeft, ChevronRight, Info, Loader2, FileText, Columns3, BookOpen } from "lucide-react";
import { fileUrl, formatDate } from "@/lib/api";

export const PageHeader = ({ eyebrow, title, children, testId = "page-header" }) => (
  <section data-testid={testId} className="relative border-b border-[#DCE5E8] grid-bg overflow-hidden">
    <div className="container-x py-14 md:py-20 rise relative">
      {eyebrow && <p className="eyebrow">{eyebrow}</p>}
      <h1 className="mt-4 text-4xl sm:text-5xl font-extrabold leading-[1.08] max-w-3xl">{title}</h1>
      {children && <div className="mt-5 max-w-2xl text-base md:text-lg text-[#526174] leading-relaxed">{children}</div>}
    </div>
  </section>
);

export const Loading = () => (
  <div className="py-24 grid place-items-center" data-testid="loading-indicator" role="status" aria-label="Loading">
    <span className="flex items-center gap-3 text-sm font-medium text-[#64728A]"><Loader2 className="h-5 w-5 animate-spin text-[#0F766E]" />Loading…</span>
  </div>
);

export const SkeletonGrid = ({ count = 3 }) => (
  <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3" data-testid="loading-indicator" role="status" aria-label="Loading">
    {Array.from({ length: count }, (_, i) => (
      <div key={i} className="card p-6 space-y-4">
        <div className="flex items-center gap-4"><div className="skeleton h-14 w-14 !rounded-xl" /><div className="flex-1 space-y-2"><div className="skeleton h-4 w-2/3" /><div className="skeleton h-3 w-1/3" /></div></div>
        <div className="skeleton h-3 w-full" /><div className="skeleton h-3 w-4/5" /><div className="skeleton h-16 w-full !rounded-xl" />
      </div>
    ))}
  </div>
);

export const Empty = ({ title = "Nothing here yet", children }) => (
  <div data-testid="empty-state" className="card p-10 text-center">
    <p className="font-display text-lg font-semibold text-[#17243A]">{title}</p>
    {children && <div className="mt-2 text-sm text-[#64728A]">{children}</div>}
  </div>
);

export const AffiliateNotice = ({ testId = "affiliate-disclosure" }) => (
  <aside data-testid={testId} className="flex gap-3 rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
    <Info className="h-5 w-5 shrink-0 text-amber-600" />
    <p>
      <strong>Affiliate disclosure:</strong> This page contains affiliate links. SaaSPivora may earn a commission from qualifying referrals at no extra cost to you. This does not automatically determine our recommendations.{" "}
      <Link to="/legal/affiliate-disclosure" className="underline font-medium">Read our disclosure</Link>.
    </p>
  </aside>
);

export function Pagination({ page, pages, onChange }) {
  if (pages <= 1) return null;
  return (
    <nav aria-label="Pagination" data-testid="pagination" className="flex items-center justify-center gap-2 mt-10">
      <button className="btn-secondary btn-sm" disabled={page <= 1} onClick={() => onChange(page - 1)} data-testid="pagination-prev"><ChevronLeft className="h-4 w-4" />Prev</button>
      {Array.from({ length: pages }, (_, i) => i + 1).map((p) => (
        <button key={p} onClick={() => onChange(p)} aria-current={p === page ? "page" : undefined} data-testid={`pagination-page-${p}`}
          className={`h-9 w-9 rounded-full text-sm font-semibold transition-colors ${p === page ? "bg-[#17243A] text-white" : "text-[#17243A] hover:bg-[#E7EDEF]"}`}>{p}</button>
      ))}
      <button className="btn-secondary btn-sm" disabled={page >= pages} onClick={() => onChange(page + 1)} data-testid="pagination-next">Next<ChevronRight className="h-4 w-4" /></button>
    </nav>
  );
}

const KIND = { comparison: [Columns3, "Comparison"], review: [FileText, "Review"], guide: [BookOpen, "Guide"], news: [FileText, "News"] };

export function ContentCard({ item, testId }) {
  const [Icon] = KIND[item.kind] || KIND.guide;
  return (
    <Link to={item.href} data-testid={testId} className="card card-hover group flex flex-col overflow-hidden h-full">
      {item.image ? (
        <img src={fileUrl(item.image)} alt={item.alt || ""} loading="lazy" className="h-44 w-full object-cover" />
      ) : (
        <div className="h-28 bg-gradient-to-br from-[#F1F4F6] to-[#E6EDF0] flex items-end p-5 relative overflow-hidden">
          <span className="absolute inset-0 dot-texture opacity-60" aria-hidden="true" />
          <span className="icon-tile h-10 w-10 bg-white relative"><Icon className="h-5 w-5" /></span>
        </div>
      )}
      <div className="p-6 flex flex-col flex-1">
        <p className="eyebrow">{item.label}</p>
        <h3 className="mt-2.5 text-lg font-bold leading-snug group-hover:text-[#3F5E7A] transition-colors">{item.title}</h3>
        <p className="mt-2 text-sm text-[#526174] line-clamp-3 flex-1 leading-relaxed">{item.excerpt}</p>
        {item.date && <p className="mt-5 pt-4 border-t border-[#EEF2F4] text-xs text-[#64728A]">{formatDate(item.date)}</p>}
      </div>
    </Link>
  );
}

export const articleToItem = (a) => ({ kind: a.article_type, label: a.blog_category, title: a.title, excerpt: a.excerpt, date: a.published_at, image: a.cover_image, alt: a.cover_alt, href: `/blog/${a.slug}` });
